/**
 * Type definitions for variants
 */
export type VariantOption = {
  variantKey?: string;
  text: string;
  variantType: string;
  associatedVariant?: string;
  image?: string;
  quantity?: number;
  price?: number;
  compareAtPrice?: number;
};

export type VariantsObject = Record<string, VariantOption>;

/**
 * Generates all possible variant combinations based on a primary combinator
 * @param variants - The original variants object
 * @param primaryCombinator - The primary variant type to combine (e.g., "Color")
 * @param defaultValues - Default values for the generated variants
 * @returns An expanded variants object with all combinations
 */
export const generateVariantCombinations = (
  variants: VariantsObject,
  primaryCombinator: string,
  defaultValues: {
    image?: string;
    quantity?: number;
    price?: number;
    compareAtPrice?: number;
  } = {}
): VariantsObject => {
  // Group variants by their type
  const variantsByType: Record<string, any[]> = {};

  // Extract existing variants by their type
  Object.entries(variants).forEach(([key, variant]) => {
    const { variantType } = variant;
    if (!variantsByType[variantType]) {
      variantsByType[variantType] = [];
    }
    variantsByType[variantType].push({ key, ...variant });
  });

  // If the primary combinator doesn't exist or there are no other variant types, return the original variants
  if (!variantsByType[primaryCombinator] || Object.keys(variantsByType).length <= 1) {
    return variants;
  }

  // Get the variants of the primary combinator type
  const primaryVariants = variantsByType[primaryCombinator];

  // Get all other variant types (excluding the primary combinator)
  const otherVariantTypes = Object.keys(variantsByType).filter(type => type !== primaryCombinator);

  // Create a new variants object - we'll start with non-primary variants only
  const newVariants: VariantsObject = {};

  // Keep only the original non-primary variants
  Object.entries(variants).forEach(([key, variant]) => {
    if (variant.variantType !== primaryCombinator) {
      newVariants[key] = { ...variant };
    }
  });

  // Generate combinations starting from 1 (for the first variant)
  let combinationCounter = 1;

  // For each primary variant
  primaryVariants.forEach(primaryVariant => {
    const { text: primaryText, variantType: primaryType } = primaryVariant;

    // For each other variant type
    otherVariantTypes.forEach(otherType => {
      const otherTypeVariants = variantsByType[otherType];
      // For each variant of the other type
      otherTypeVariants.forEach(otherVariant => {
        const { text: otherText } = otherVariant;

        // Create a new primary variant combination
        const newKey = `${primaryType}${combinationCounter}`;

        // Check if we already have a combination with these exact values
        const isDuplicate = Object.values(newVariants).some(
          existingVariant =>
            existingVariant.variantType === primaryType &&
            existingVariant.text === primaryText &&
            existingVariant.associatedVariant === otherText
        );

        if (!isDuplicate) {
          // Get original variant if it exists
          const originalVariant =
            primaryVariant.key in variants ? variants[primaryVariant.key] : null;

          newVariants[newKey] = {
            text: primaryText,
            variantType: primaryType,
            variantKey: newKey,
            associatedVariant: otherText,

            // Preserve values from original variant if it exists, otherwise use defaults
            quantity:
              (originalVariant?.quantity !== undefined)
                ? originalVariant.quantity
                : defaultValues.quantity || 0,

            price:
              (originalVariant?.price !== undefined)
                ? originalVariant.price
                : defaultValues.price || 0,

            compareAtPrice:
              (originalVariant?.compareAtPrice !== undefined)
                ? originalVariant.compareAtPrice
                : defaultValues.compareAtPrice || 0,

            image:
              (originalVariant?.image !== undefined)
                ? originalVariant.image
                : defaultValues.image || '',
          };

          combinationCounter++;
        }
      });
    });
  });

  return newVariants;
};

/**
 * Example usage:
 * 
 * // This is your initial variants object
 * const variants: VariantsObject = {
 *   "Color1": {
 *     "text": "Red",
 *     "variantType": "Color",
 *   },
 *   "Color2": {
 *     "text": "White",
 *     "variantType": "Color"
 *   },
 *   "Size1": {
 *     "text": "XL",
 *     "variantType": "Size"
 *   },
 *   "Size2": {
 *     "text": "L",
 *     "variantType": "Size"
 *   }
 * };
 * 
 * // Generate all combinations with Color as the primary combinator
 * const expandedVariants = generateVariantCombinations(
 *   variants, 
 *   "Color",
 *   { image: "", quantity: 10, price: 19.99, compareAtPrice: 29.99 }
 * );
 * 
 * // Result will be:
 * // {
 * //   "Size1": { "text": "XL", "variantType": "Size" },
 * //   "Size2": { "text": "L", "variantType": "Size" },
 * //   "Color1": { 
 * //     "text": "Red", 
 * //     "variantType": "Color", 
 * //     "associatedVariant": "XL",
 * //     "quantity": 10, 
 * //     "price": 19.99, 
 * //     "compareAtPrice": 29.99 
 * //   },
 * //   "Color2": { 
 * //     "text": "Red", 
 * //     "variantType": "Color", 
 * //     "associatedVariant": "L",
 * //     "image": "", 
 * //     "quantity": 10, 
 * //     "price": 19.99, 
 * //     "compareAtPrice": 29.99 
 * //   },
 * //   "Color3": { 
 * //     "text": "White", 
 * //     "variantType": "Color", 
 * //     "associatedVariant": "XL",
 * //     "image": "", 
 * //     "quantity": 10, 
 * //     "price": 19.99, 
 * //     "compareAtPrice": 29.99 
 * //   },
 * //   "Color4": { 
 * //     "text": "White", 
 * //     "variantType": "Color", 
 * //     "associatedVariant": "L",
 * //     "image": "", 
 * //     "quantity": 10, 
 * //     "price": 19.99, 
 * //     "compareAtPrice": 29.99 
 * //   }
 * // }
 */



export const extractFields = (obj: any, field: string) => {
  const result = {} as any;
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && key.startsWith(field)) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Generates an array of variant combinations formatted for ProductVariantCombinationCard
 * @param variantValues - Object containing all variant values (with primary and associated secondary variants)
 * @param productName - Name of the product for SKU generation
 * @returns Array of objects ready to be used with ProductVariantCombinationCard
 */
export const getVariantCombinationsForCards = (
  primaryCombinator: string,
  variants: any
): VariantOption[] => {
  const primaryVariant = extractFields(variants, primaryCombinator);
  const convertObjectValuesToArray = Object.entries(primaryVariant).map(([key, value]) => ({
    variantKey: key,
    ...(value as object) // Cast value to object before spreading
  }));
  return convertObjectValuesToArray as VariantOption[];
};

/**
 * Extracts unique variant types and values from a variant values object with combinations
 * This function is the reverse of generateVariantCombinations
 * 
 * @param variantValues - The expanded variant values object with combinations and associations
 * @returns A simplified object with only unique variant types and values
 * 
 * Example:
 * Input (expanded variants with associations):
 * {
 *   "Size1": { "text": "XL", "variantType": "Size" },
 *   "Size2": { "text": "L", "variantType": "Size" },
 *   "Color1": { "text": "Red", "variantType": "Color", "associatedVariant": "XL", ... },
 *   "Color2": { "text": "Red", "variantType": "Color", "associatedVariant": "L", ... },
 *   "Color3": { "text": "White", "variantType": "Color", "associatedVariant": "XL", ... },
 *   "Color4": { "text": "White", "variantType": "Color", "associatedVariant": "L", ... }
 * }
 * 
 * Output (unique variants):
 * {
 *   "Color1": { "text": "Red", "variantType": "Color" },
 *   "Color2": { "text": "White", "variantType": "Color" },
 *   "Size1": { "text": "XL", "variantType": "Size" },
 *   "Size2": { "text": "L", "variantType": "Size" }
 * }
 */
export const extractUniqueVariants = (
    variantValues: Record<string, any>
): Record<string, any> => {
    if (!variantValues || Object.keys(variantValues).length === 0) {
        return {};
    }

    // Group unique values by variant type
    const uniqueVariantsByType: Record<string, Set<string>> = {};
    
    // Extract all unique variant values for each type
    Object.entries(variantValues).forEach(([key, data]) => {
        const { variantType, text } = data as { variantType: string; text: string };
        
        if (!uniqueVariantsByType[variantType]) {
            uniqueVariantsByType[variantType] = new Set();
        }
        
        uniqueVariantsByType[variantType].add(text);
    });
    
    // Create the result object with the format of the original input
    const result: Record<string, any> = {};
    
    // Convert the sets back to an object with the expected structure
    Object.entries(uniqueVariantsByType).forEach(([variantType, values]) => {
        let index = 1;
        Array.from(values).forEach(text => {
            const key = `${variantType}${index}`;
            result[key] = {
                text,
                variantType
            };
            index++;
        });
    });
    
    return result;
}; 